import { useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { TopStatusBar } from "@/components/TopStatusBar";
import { useAuth } from "@/hooks/useAuth";
import { getAppEnv } from "@/lib/appEnv";
import { resolveSessionBannerPreview } from "@/lib/sessionBannerPreview";

const PREVIEW_ORG_NAME = "Conta demo (preview)";
const PREVIEW_EMAIL = "staff@preview.local";
const PREVIEW_STAFF_EMAIL = "staff@preview.local";

const DEMO_ENV_TONE = "bg-amber-600 text-white dark:bg-amber-700";
const DEV_ENV_TONE = "bg-primary text-primary-foreground";

export function AppContextBanner(): ReactElement | null {
  const { t } = useTranslation();
  const { user, isLoading } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const preview = resolveSessionBannerPreview();

  const staffImpersonation = user?.staffImpersonation ?? null;
  const isDemoOrg = user?.organization?.isDemo === true;

  const impersonating = useMemo(() => {
    if (preview === "impersonation" || preview === "both") return true;
    return staffImpersonation !== null;
  }, [preview, staffImpersonation]);

  const banner = useMemo(() => {
    const env = getAppEnv();
    const isDemoEnv = env === "demo";
    const isDevEnv = env === "development" || env === "local";
    const previewDemoAccount = preview === "demo" || preview === "both";

    const orgName = user?.organization?.name ?? PREVIEW_ORG_NAME;
    const email = user?.email ?? PREVIEW_EMAIL;

    const hasEnvBanner = isDemoEnv || isDevEnv;
    const hasSessionInfo = impersonating || isDemoOrg || previewDemoAccount;

    if (!hasEnvBanner && !hasSessionInfo) return null;

    const parts: string[] = [];

    if (isDemoEnv) {
      parts.push(t("appBanner.demoEnv"));
    } else if (isDevEnv) {
      parts.push(t("appBanner.devEnv"));
    }

    if (impersonating) {
      const staffEmail =
        staffImpersonation?.staffEmail ??
        (preview === "impersonation" || preview === "both" ? PREVIEW_STAFF_EMAIL : null);
      const readOnly = staffImpersonation?.readOnly !== false;

      parts.push(
        staffEmail
          ? t(
              readOnly
                ? "appBanner.impersonationReadOnlyWithStaff"
                : "appBanner.impersonationWithStaff",
              {
                staffEmail,
                orgName,
                email,
              },
            )
          : t(readOnly ? "appBanner.impersonationReadOnly" : "appBanner.impersonation", {
              orgName,
              email,
            }),
      );
    } else if (isDemoOrg || previewDemoAccount) {
      parts.push(
        t("appBanner.demoAccount", {
          orgName,
        }),
      );
    }

    if (parts.length === 0) return null;

    return {
      label: parts.join(" · "),
      tone: isDemoEnv ? DEMO_ENV_TONE : isDevEnv ? DEV_ENV_TONE : DEMO_ENV_TONE,
      dataStatus: impersonating
        ? "impersonation"
        : isDemoEnv
          ? "demo"
          : isDevEnv
            ? "development"
            : "demo-account",
    };
  }, [
    impersonating,
    isDemoOrg,
    preview,
    staffImpersonation?.staffEmail,
    staffImpersonation?.readOnly,
    t,
    user?.email,
    user?.organization?.name,
  ]);

  if (isLoading && !preview) return null;
  if (!banner || dismissed) return null;

  return (
    <TopStatusBar
      data-status={banner.dataStatus}
      tone={banner.tone}
      label={banner.label}
      dismissible
      onDismiss={() => setDismissed(true)}
      closeAriaLabel={t("common.close")}
    />
  );
}
